<script setup lang="ts">
import Footer from '@/components/common/Footer.vue'
import LoadingBox from '@/components/zoom-diagnostic/Loading.vue'
import ControlButton from '@/components/room/ControlButton.vue'
import GetStarted from '@/components/zoom-diagnostic/GetStarted.vue'
import CheckPermissions from '@/components/zoom-diagnostic/CheckPermissions.vue'
import PermissionError from '@/components/zoom-diagnostic/PermissionError.vue'
import CameraTest from '@/components/zoom-diagnostic/CameraTest.vue'
import AudioTest from '@/components/zoom-diagnostic/AudioTest.vue'
import AudioTestRoom from '@/components/video-diagnostic/AudioTest.vue'
import Connectivity from '@/components/zoom-diagnostic/Connectivity.vue'
import Result from '@/components/zoom-diagnostic/Result.vue'
import ZoomDisconnectPopup from '@/components/room/ZoomDisconnectPopup.vue'
import NoAccessPopup from '@/components/room/NoAccessPopup.vue'
import {
  useConnection,
  zoomDiagnosticDisconnect,
} from '@/composables/zoom-diagnostic/useConnection'
import { permissions } from '@/composables/video-diagnostic/useCheckPermissions'
import { IUpdateTopic } from '@/types/interfaces/video-diagnostic.interface'
import { Stage, Topic } from '@/types/enums/zoom-diagnostic.enum'
import { useRoute, useRouter } from 'vue-router'
import { reactive, ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CommonState from '@/services/locale-state'
import useAuth, { getToken } from '@/composables/api/useAuth'
import { isIOSMobile } from '@/helpers/zoom-device.helper'
import { useRoomStore } from '@/stores'
import { catchJson } from '@/helpers/transform.helper'

const i18n = useI18n()
const route = useRoute()
const roomStore = useRoomStore()
const router = useRouter()
const { loginWithToken } = useAuth()

const topic = reactive({
  deviceSetup: false,
  connectivity: false,
  quality: false,
  results: false,
})
const stage = ref(0)
const supportedBrowser = ref(true)
const isInProgress = ref(true)
const finishedTest = ref(false)
const isLoading = ref(false)
const isMainEngine = ref(false)
const isIOS = ref(false)
const isNoToken = ref(false)
const header = computed(() => i18n.t('zoom-diagnostic.test-are-done'))
const message = computed(() => i18n.t('zoom-diagnostic.wait-report'))

let stopWatchStage

stopWatchStage = watch(stage, (stage) => {
  if (stage === Stage.RESULTS) {
    finishedTest.value = true
    isLoading.value = true
    setTimeout(() => {
      isLoading.value = false
      const objDiv = document.getElementById('scrollContainer')
      objDiv.scrollTop = objDiv.scrollHeight
      stopWatchStage()
    }, 1500)
  }
})

watch(isLoading, () => {
  setTimeout(() => {
    const objDiv = document.getElementById('scrollContainer')
    const footer = document.getElementById('footer')
    objDiv.scrollTop = objDiv.scrollHeight
    /**
     * scroll to result header after get the result
     */
    if (objDiv && footer && topic.results) {
      objDiv.scrollTop = objDiv.scrollHeight - (objDiv.offsetHeight + footer.offsetHeight)
    }
  }, 20)
})

const scrollDown = (id: number) => {
  stage.value = id
  const el = document.getElementById(`${id}`)
  const firstEl = document.getElementById('0')
  if (el) {
    const offset =
      id === Stage.RESULTS
        ? firstEl.offsetTop + firstEl.offsetHeight * 0.5
        : el.offsetTop + el.offsetHeight * 0.5
    const isMobile = screen.width <= 640
    if (el?.parentElement) {
      el.parentElement.style.transform = isMobile
        ? 'none'
        : `translateY(calc(50vh - ${offset}px + 50px))`
    }
  }
}

const updateTopic = (value: IUpdateTopic) => {
  switch (value.topic) {
    case Topic.DEVICE_SETUP:
      if (value.active) {
        topic.deviceSetup = true
      } else {
        topic.deviceSetup = false
      }
      break
    case Topic.CONNECTIVITY:
      if (value.active) {
        topic.connectivity = true
      } else {
        topic.connectivity = false
      }
      break
    case Topic.RESULTS:
      if (value.active) {
        topic.results = true
      } else {
        topic.results = false
      }
      break
  }
}

const setLanguage = () => {
  if (route.query.language) {
    switch (route.query.language) {
      case 'th': {
        CommonState.setLocale('th')
        i18n.locale.value = 'th'
        break
      }
      case 'vn': {
        CommonState.setLocale('vn')
        i18n.locale.value = 'vn'
        break
      }
      case 'en': {
        CommonState.setLocale('en')
        i18n.locale.value = 'en'
        break
      }
    }
  }
}

const setCountry = () => {
  if (route.query.country) {
    switch (route.query.country) {
      case 'th': {
        CommonState.setCountry('th')
        break
      }
      case 'vn': {
        CommonState.setCountry('vn')
        break
      }
    }
  }
}

onMounted(async () => {
  isIOS.value = isIOSMobile()
  setLanguage()
  setCountry()
  scrollDown(0)
  if (!route.query.token) {
    if (getToken()) {
      await roomStore.connect()
    } else {
      isNoToken.value = true
    }
  } else {
    const res = await loginWithToken(route.query.token as string)
    if (res.status === 200) {
      await roomStore.connect()
    } else {
      const errorData = catchJson(res.message?.error)
      const timeAttend = errorData?.now
      const message = res.message?.message
      router.push({
        name: 'join-fail',
        query: {
          from: location.href,
          code: res.message?.statusCode,
          timeAttend,
          message,
          error: res.message?.error,
          token: route.query.token,
        },
      })
    }
  }
  isMainEngine.value = roomStore.room.isMainEngine
  await useConnection({
    token: roomStore.token,
    roomName: roomStore.room.id,
    name: roomStore.localParticipantInfo?.name || '',
  })
})
</script>

<template>
  <DefaultLayout
    have-navbar
    can-change-language
    theme="light"
    have-engine
    :is-main-engine="isMainEngine"
  >
    <div
      id="scrollContainer"
      :class="finishedTest ? 'overflow-auto' : 'overflow-hidden'"
      class="h-screen"
    >
      <section class="wizard-wrapper">
        <div class="container font-bold mx-auto px-40">
          <div class="flex flex-row text-xs xl:text-base">
            <div :class="topic.deviceSetup ? 'opacity-100' : 'opacity-30'" class="mr-10 xl:mr-20">
              {{ $t('zoom-diagnostic.device-software-setup') }}
              <span class="ml-4 xl:ml-10">></span>
            </div>
            <div :class="topic.connectivity ? 'opacity-100' : 'opacity-30'" class="mr-10 xl:mr-20">
              {{ $t('zoom-diagnostic.connectivity') }}
              <span class="ml-4 xl:ml-10">></span>
            </div>
            <div :class="topic.results ? 'opacity-100' : 'opacity-30'">
              {{ $t('zoom-diagnostic.get-result') }}
            </div>
          </div>
        </div>
        <div class="w- mt-10 bg-gray-200 rounded-full h-1 dark:bg-gray-700">
          <div
            class="bg-primary-color h-1 rounded-full duration-1000"
            :style="`width: ${(stage / Stage.RESULTS) * 100}%`"
          ></div>
        </div>
      </section>

      <div v-if="isLoading" class="loading-container">
        <LoadingBox :stage="Stage.CONNECTIVITY" :header="header" :message="message"></LoadingBox>
      </div>

      <div v-show="!isLoading" class="scrollContainer">
        <GetStarted :stage="stage" @next-stage="scrollDown"></GetStarted>

        <CheckPermissions
          :stage="stage"
          @next-stage="scrollDown"
          @update-topic="updateTopic"
        ></CheckPermissions>

        <PermissionError v-if="!permissions" :stage="stage"></PermissionError>

        <CameraTest :stage="stage" @next-stage="scrollDown"></CameraTest>

        <AudioTest v-if="!isIOS" :stage="stage" @next-stage="scrollDown"></AudioTest>

        <AudioTestRoom v-if="isIOS" :stage="stage" @next-stage="scrollDown"></AudioTestRoom>

        <Connectivity
          :stage="stage"
          @inprogress="isInProgress = false"
          @next-stage="scrollDown"
          @update-topic="updateTopic"
        ></Connectivity>

        <Result
          v-if="!isInProgress"
          :stage="stage"
          @next-stage="scrollDown"
          @update-topic="updateTopic"
        ></Result>

        <Footer v-if="stage === Stage.RESULTS && topic.results" />
      </div>

      <section v-if="stage !== Stage.RESULTS" class="hidden md:block arrow-container">
        <ControlButton
          :class="stage === 0 ? 'opacity-0 pointer-events-none' : ''"
          class="arrow-container__button mb-4"
          icon="line-arrow-up"
          @click="
            scrollDown(
              permissions && stage === Stage.CAMERA_TEST ? Stage.CHECK_PERMISSIONS : stage - 1,
            )
          "
        />
        <ControlButton
          :class="
            [Stage.CHECK_PERMISSIONS, Stage.PERMISSION_ERROR, Stage.RESULTS].includes(stage) ||
            !supportedBrowser ||
            (isInProgress && stage === Stage.CONNECTIVITY)
              ? 'opacity-0 pointer-events-none'
              : ''
          "
          class="arrow-container__button"
          icon="line-arrow-down"
          @click="scrollDown(stage + 1)"
        />
      </section>

      <Teleport to="body">
        <ZoomDisconnectPopup
          v-model="zoomDiagnosticDisconnect"
          :exit-path="roomStore.room.exitPath"
        />
      </Teleport>

      <Teleport to="body">
        <NoAccessPopup v-model="isNoToken" />
      </Teleport>
    </div>
  </DefaultLayout>
</template>

<style lang="scss" scoped>
.bg-primary-color {
  background-color: #f67519;
}

.arrow-container {
  position: fixed;
  top: 80%;
  right: 3%;
  &__button {
    color: black !important;
    background-color: white !important;
    border: 1px solid var(--compartment-body-color);
    &:hover {
      background-color: var(--bg-chat-msg) !important;
      transition: 0.4s;
    }
  }
  & > div {
    width: 40px !important;
    height: 40px !important;
  }
}

.loading-container {
  transform: translateY(40vh);
}

.scrollContainer {
  transition: all 1s ease;
  position: relative;
  transform: translateY(50vh);
}

@include media-breakpoint-down-custom(600px) {
  .scrollContainer {
    transition: all 0s ease;
    transform: initial;
  }
}
.wizard-wrapper {
  z-index: 1;
  @apply hidden md:block fixed w-full bg-white pt-10 text-base;
}
</style>
